import React, { useState, useEffect } from 'react';
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BookOpen, Search, Plus, Calendar as CalendarIcon, ArrowLeft, Save, Trash2, Star, Heart, Zap, Cloud, Sun, Moon, Sparkles, Image as ImageIcon, X, Filter, SortDesc } from "lucide-react";
import { SparkleGuide } from './guide/SparkleGuide';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { useUniversalTracking } from '@/hooks/useUniversalTracking';
import { ContextualNotificationPrompt } from "@/components/notifications/ContextualNotificationPrompt";
interface QuantumJournalProps {
  isOpen: boolean;
  onClose: () => void;
}
interface JournalEntry {
  id: string;
  title?: string;
  content: string;
  mood?: string;
  photos?: any;
  created_at: string;
  updated_at: string;
}
type MoodType = 'grateful' | 'peaceful' | 'energised' | 'contemplative' | 'releasing' | 'inspired' | 'grounded' | 'flowing';
const QuantumJournal: React.FC<QuantumJournalProps> = ({
  isOpen,
  onClose
}) => {
  const { logExercise } = useUniversalTracking();
  const [currentView, setCurrentView] = useState<'list' | 'write' | 'edit' | 'worry-diary'>('list');
  const [journalMode, setJournalMode] = useState<'free-flow' | 'worry-diary'>('free-flow');
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [currentEntry, setCurrentEntry] = useState<JournalEntry | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMood, setFilterMood] = useState<string>('');
  const [filterDate, setFilterDate] = useState<Date | undefined>();
  const [sortBy, setSortBy] = useState<'date' | 'title'>('date');
  const [isLoading, setIsLoading] = useState(false);
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('');
  const [selectedMood, setSelectedMood] = useState<MoodType | ''>('');
  const [selectedPhotos, setSelectedPhotos] = useState<string[]>([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [entryDate, setEntryDate] = useState<Date | undefined>(new Date());

  // Worry diary specific states
  const [worryPrompts, setWorryPrompts] = useState({
    currentWorries: '',
    worryImpact: '',
    evidenceFor: '',
    evidenceAgainst: '',
    alternativePerspective: '',
    actionSteps: '',
    selfCompassion: ''
  });
  const moods = [{
    value: 'grateful',
    label: 'Grateful',
    icon: Heart,
    color: 'text-accent'
  }, {
    value: 'peaceful',
    label: 'Peaceful',
    icon: Cloud,
    color: 'text-muted'
  }, {
    value: 'energised',
    label: 'Energised',
    icon: Zap,
    color: 'text-primary'
  }, {
    value: 'contemplative',
    label: 'Contemplative',
    icon: Moon,
    color: 'text-secondary'
  }, {
    value: 'releasing',
    label: 'Releasing',
    icon: Cloud,
    color: 'text-muted-foreground'
  }, {
    value: 'inspired',
    label: 'Inspired',
    icon: Sparkles,
    color: 'text-primary'
  }, {
    value: 'grounded',
    label: 'Grounded',
    icon: Star,
    color: 'text-secondary'
  }, {
    value: 'flowing',
    label: 'Flowing',
    icon: Sun,
    color: 'text-accent'
  }];
  useEffect(() => {
    if (isOpen) {
      setCurrentView('list'); // Always start with the main list view
      loadEntries();
    }
  }, [isOpen, sortBy]);
  const loadEntries = async () => {
    setIsLoading(true);
    try {
      const {
        data: {
          user
        }
      } = await supabase.auth.getUser();
      if (!user) return;
      let query = supabase.from('journal_entries').select('*').eq('user_id', user.id);
      if (sortBy === 'date') {
        query = query.order('created_at', {
          ascending: false
        });
      } else {
        query = query.order('title', {
          ascending: true
        });
      }
      const {
        data,
        error
      } = await query;
      if (error) throw error;
      const processedData = (data || []).map(entry => ({
        ...entry,
        photos: Array.isArray(entry.photos) ? entry.photos : []
      }));
      setEntries(processedData);
    } catch (error) {
      toast.error('Failed to load journal entries');
    } finally {
      setIsLoading(false);
    }
  };
  const saveEntry = async () => {
    if (!content.trim() && selectedPhotos.length === 0) {
      toast.error('Please write something or add a photo before saving');
      return;
    }
    setIsLoading(true);
    try {
      const {
        data: {
          user
        }
      } = await supabase.auth.getUser();
      if (!user) return;
      const entryTitle = title.trim() || content.split('\n')[0].substring(0, 50) + (content.length > 50 ? '...' : '');
      const entryData = {
        user_id: user.id,
        title: entryTitle,
        content: content.trim(),
        mood: selectedMood || null,
        photos: selectedPhotos.length > 0 ? selectedPhotos : null,
        created_at: entryDate ? entryDate.toISOString() : new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      if (currentEntry) {
        const {
          error
        } = await supabase.from('journal_entries').update(entryData).eq('id', currentEntry.id);
        if (error) throw error;
        toast.success('Entry updated successfully');
      } else {
        const {
          error
        } = await supabase.from('journal_entries').insert(entryData);
        if (error) throw error;
        toast.success('Entry saved successfully');
      }
      setContent('');
      setTitle('');
      setSelectedMood('');
      setSelectedPhotos([]);
      setEntryDate(new Date());
      setCurrentEntry(null);
      setCurrentView('list');
      loadEntries();
    } catch (error) {
      toast.error('Failed to save entry');
    } finally {
      setIsLoading(false);
    }
  };
  const saveWorryDiaryEntry = async () => {
    const worryContent = Object.values(worryPrompts).join('\n\n').trim();
    if (!worryContent) {
      toast.error('Please complete at least one section before saving');
      return;
    }
    setIsLoading(true);
    try {
      const {
        data: {
          user
        }
      } = await supabase.auth.getUser();
      if (!user) return;
      const entryData = {
        user_id: user.id,
        title: `Evening Worry Diary - ${new Date().toLocaleDateString()}`,
        content: `WORRY DIARY ENTRY

Current Worries & Fears:
${worryPrompts.currentWorries}

How these worries are impacting me:
${worryPrompts.worryImpact}

Evidence that supports my worry:
${worryPrompts.evidenceFor}

Evidence against my worry:
${worryPrompts.evidenceAgainst}

Alternative perspective:
${worryPrompts.alternativePerspective}

Action steps I can take:
${worryPrompts.actionSteps}

Self-compassion & kindness:
${worryPrompts.selfCompassion}`,
        mood: 'contemplative',
        photos: []
      };
      const {
        error
      } = await supabase.from('journal_entries').insert(entryData);
      if (error) throw error;
      
      // Track worry diary completion
      await logExercise({
        exercise_type: 'worry_diary',
        exercise_name: 'Evening Worry Diary',
        category: 'anxiety',
        duration_minutes: 15,
        completion_data: {
          sections_completed: Object.values(worryPrompts).filter(v => v.trim()).length
        },
        xp_earned: 20
      });
      
      toast.success('Worry diary entry saved successfully');
      setWorryPrompts({
        currentWorries: '',
        worryImpact: '',
        evidenceFor: '',
        evidenceAgainst: '',
        alternativePerspective: '',
        actionSteps: '',
        selfCompassion: ''
      });
      setCurrentView('list');
      loadEntries();
    } catch (error) {
      toast.error('Failed to save worry diary');
    } finally {
      setIsLoading(false);
    }
  };
  const deleteEntry = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;
    try {
      const {
        error
      } = await supabase.from('journal_entries').delete().eq('id', id);
      if (error) throw error;
      toast.success('Entry deleted successfully');
      loadEntries();
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };
  const startNewWorryDiary = () => {
    setWorryPrompts({
      currentWorries: '',
      worryImpact: '',
      evidenceFor: '',
      evidenceAgainst: '',
      alternativePerspective: '',
      actionSteps: '',
      selfCompassion: ''
    });
    setCurrentView('worry-diary');
  };
  const startNewEntry = () => {
    setContent('');
    setTitle('');
    setSelectedMood('');
    setSelectedPhotos([]);
    setEntryDate(new Date());
    setCurrentEntry(null);
    if (journalMode === 'worry-diary') {
      startNewWorryDiary();
    } else {
      setCurrentView('write');
    }
  };
  const editEntry = (entry: JournalEntry) => {
    setContent(entry.content);
    setTitle(entry.title || '');
    setSelectedMood(entry.mood as MoodType || '');
    setSelectedPhotos(Array.isArray(entry.photos) ? entry.photos : []);
    setEntryDate(new Date(entry.created_at));
    setCurrentEntry(entry);
    setCurrentView('edit');
  };
  const handlePhotoUpload = async (files: FileList | null) => {
    if (!files) return;
    setUploadingPhoto(true);
    try {
      const {
        data: {
          user
        }
      } = await supabase.auth.getUser();
      if (!user) return;
      const uploadPromises = Array.from(files).map(async file => {
        const fileExt = file.name.split('.').pop();
        const fileName = `${user.id}/${Date.now()}_${Math.random()}.${fileExt}`;
        const {
          data,
          error
        } = await supabase.storage.from('manifestation-media').upload(fileName, file);
        if (error) throw error;
        const {
          data: urlData
        } = supabase.storage.from('manifestation-media').getPublicUrl(data.path);
        return urlData.publicUrl;
      });
      const uploadedUrls = await Promise.all(uploadPromises);
      setSelectedPhotos(prev => [...prev, ...uploadedUrls]);
      toast.success(`${uploadedUrls.length} photo(s) uploaded successfully`);
    } catch (error) {
      toast.error('Failed to upload photos');
    } finally {
      setUploadingPhoto(false);
    }
  };
  const removePhoto = (photoUrl: string) => {
    setSelectedPhotos(prev => prev.filter(url => url !== photoUrl));
  };
  const filteredEntries = entries.filter(entry => {
    const matchesSearch = entry.content.toLowerCase().includes(searchTerm.toLowerCase()) || entry.title && entry.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesMood = !filterMood || filterMood === "all" || entry.mood === filterMood;
    const matchesDate = !filterDate || new Date(entry.created_at).toDateString() === filterDate.toDateString();
    return matchesSearch && matchesMood && matchesDate;
  });
  const getMoodIcon = (mood: string) => {
    const moodConfig = moods.find(m => m.value === mood);
    if (!moodConfig) return null;
    const IconComponent = moodConfig.icon;
    return <IconComponent className={`h-4 w-4 ${moodConfig.color}`} />;
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  if (!isOpen) return null;
  const renderWorryDiaryView = () => <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="secondary" size="sm" onClick={() => setCurrentView('list')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Entries
        </Button>
        <h3 className="text-xl font-semibold">Evening Worry Diary</h3>
        <div className="w-20" />
      </div>

      <Card className="p-4 bg-gradient-to-br from-background to-muted/20">
        <div className="text-center mb-4">
          <Moon className="h-8 w-8 mx-auto mb-3 text-primary" />
          <h4 className="text-lg font-semibold mb-2">This Is Your Safe Space to Process Worries</h4>
          <p className="text-sm text-muted-foreground mb-3">
            Set aside 15-20 minutes each evening to voice your fears and work through them with compassion.
            This practice helps transform worry into wisdom.
          </p>
          <Badge variant="outline" className="text-xs">
            ⏰ Recommended: 15-20 minutes
          </Badge>
        </div>
      </Card>

      <div className="space-y-4">
        <Card className="p-4">
          <Label className="text-sm font-semibold text-foreground mb-3 block">
            1. What worries or fears are on your mind today?
          </Label>
          <p className="text-xs text-muted-foreground mb-3">
            Voice them fully. Let them out without judgment. What scenarios are playing in your mind?
          </p>
          <Textarea value={worryPrompts.currentWorries} onChange={e => setWorryPrompts(prev => ({
          ...prev,
          currentWorries: e.target.value
        }))} placeholder="Today I'm worried about... I keep thinking about... I'm afraid that..." className="min-h-[100px] bg-muted/30" />
        </Card>

        <Card className="p-4">
          <Label className="text-sm font-semibold text-foreground mb-3 block">
            2. How are these worries impacting you right now?
          </Label>
          <p className="text-xs text-muted-foreground mb-3">
            Notice the physical sensations, emotions, and thoughts. Where do you feel it in your body?
          </p>
          <Textarea value={worryPrompts.worryImpact} onChange={e => setWorryPrompts(prev => ({
          ...prev,
          worryImpact: e.target.value
        }))} placeholder="I feel tense in my... My heart is... I notice thoughts like... My energy feels..." className="min-h-[80px] bg-muted/30" />
        </Card>

        <Card className="p-4">
          <Label className="text-sm font-semibold text-foreground mb-3 block">
            3. What evidence supports this worry?
          </Label>
          <p className="text-xs text-muted-foreground mb-3">
            Be honest about what facts or experiences fuel this fear. What has happened before?
          </p>
          <Textarea value={worryPrompts.evidenceFor} onChange={e => setWorryPrompts(prev => ({
          ...prev,
          evidenceFor: e.target.value
        }))} placeholder="This worry feels real because... In the past, I've experienced... The facts that support this are..." className="min-h-[80px] bg-muted/30" />
        </Card>

        <Card className="p-4">
          <Label className="text-sm font-semibold text-foreground mb-3 block">
            4. What evidence challenges this worry?
          </Label>
          <p className="text-xs text-muted-foreground mb-3">
            Look for facts, past successes, or resources that contradict this fear. What else is true?
          </p>
          <Textarea value={worryPrompts.evidenceAgainst} onChange={e => setWorryPrompts(prev => ({
          ...prev,
          evidenceAgainst: e.target.value
        }))} placeholder="However, I also know that... Times when things worked out... My strengths include... Resources I have..." className="min-h-[80px] bg-muted/30" />
        </Card>

        <Card className="p-4">
          <Label className="text-sm font-semibold text-foreground mb-3 block">
            5. What would a loving friend say about this situation?
          </Label>
          <p className="text-xs text-muted-foreground mb-3">
            Imagine someone who loves you unconditionally. What perspective would they offer?
          </p>
          <Textarea value={worryPrompts.alternativePerspective} onChange={e => setWorryPrompts(prev => ({
          ...prev,
          alternativePerspective: e.target.value
        }))} placeholder="A loving friend would remind me that... They might say... Another way to see this is..." className="min-h-[80px] bg-muted/30" />
        </Card>

        <Card className="p-4">
          <Label className="text-sm font-semibold text-foreground mb-3 block">
            6. What small action can you take tomorrow?
          </Label>
          <p className="text-xs text-muted-foreground mb-3">
            Focus on what's within your control. What's one small step you can take?
          </p>
          <Textarea value={worryPrompts.actionSteps} onChange={e => setWorryPrompts(prev => ({
          ...prev,
          actionSteps: e.target.value
        }))} placeholder="Tomorrow I can... One small step would be... I have control over... I will..." className="min-h-[80px] bg-muted/30" />
        </Card>

        <Card className="p-4 bg-gradient-primary/5">
          <Label className="text-sm font-semibold text-foreground mb-3 block">
            7. What kindness do you need right now?
          </Label>
          <p className="text-xs text-muted-foreground mb-3">
            End with self-compassion. What would you say to comfort yourself? What do you need to hear?
          </p>
          <Textarea value={worryPrompts.selfCompassion} onChange={e => setWorryPrompts(prev => ({
          ...prev,
          selfCompassion: e.target.value
        }))} placeholder="It's okay that I'm worried because... I'm doing my best... I deserve kindness because... Right now I need..." className="min-h-[80px] bg-background/50" />
        </Card>
      </div>

      <div className="flex gap-2 justify-center pt-4">
        <Button variant="outline" onClick={() => setCurrentView('list')} disabled={isLoading}>
          Cancel
        </Button>
        <Button onClick={saveWorryDiaryEntry} disabled={isLoading} className="flex items-center gap-2">
          <Save className="h-4 w-4" />
          {isLoading ? 'Saving...' : 'Save Worry Diary'}
        </Button>
      </div>
    </div>;
  const renderWriteView = () => <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="secondary" size="sm" onClick={() => setCurrentView('list')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Entries
        </Button>
        <h3 className="text-xl font-semibold">
          {currentEntry ? 'Edit Entry' : 'New Entry'}
        </h3>
        <div className="w-20" />
      </div>

      <div className="mb-4">
        <Label htmlFor="entry-title" className="text-sm font-medium mb-2 block text-muted-foreground">
          Entry Title (Optional)
        </Label>
        <Input id="entry-title" value={title} onChange={e => setTitle(e.target.value)} placeholder="Give your entry a memorable title..." className="mb-4" />
      </div>

      <div className="mb-4">
        <Label className="text-sm font-medium mb-2 block text-muted-foreground">
          Entry Date
        </Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !entryDate && "text-muted-foreground")}>
              <CalendarIcon className="mr-2 h-4 w-4" />
              {entryDate ? format(entryDate, "PPP") : <span>Pick a date</span>}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar mode="single" selected={entryDate} onSelect={setEntryDate} initialFocus className={cn("p-3 pointer-events-auto")} />
          </PopoverContent>
        </Popover>
      </div>

      <Card className="p-4 bg-muted/30">
        <label className="text-sm font-medium mb-3 block text-muted-foreground">
          How are you feeling? (Optional)
        </label>
        <div className="grid grid-cols-4 gap-2">
          {moods.map(mood => {
          const IconComponent = mood.icon;
          const isSelected = selectedMood === mood.value;
          return <button key={mood.value} onClick={() => setSelectedMood(isSelected ? '' : mood.value as MoodType)} className={`p-3 rounded-lg border transition-all duration-200 flex items-center justify-center gap-2 ${isSelected ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:border-primary/50 hover:bg-muted/50'}`}>
                <IconComponent className={`h-4 w-4 ${isSelected ? 'text-primary' : mood.color}`} />
                <span className="text-xs font-medium">{mood.label}</span>
              </button>;
        })}
        </div>
      </Card>

      <Card className="p-6 bg-gradient-to-br from-background to-muted/20">
        <div className="text-center mb-6">
          <BookOpen className="h-8 w-8 mx-auto mb-3 text-primary" />
          <h4 className="text-lg font-semibold mb-2">Your Sacred Writing Space</h4>
          <p className="text-sm text-muted-foreground">
            Let your thoughts flow freely. No rules, no judgment, just pure expression.
          </p>
        </div>

        <Textarea value={content} onChange={e => setContent(e.target.value)} placeholder="What wants to be expressed today? Let it flow..." className="min-h-[300px] border-0 bg-transparent text-base leading-relaxed resize-none" style={{
        boxShadow: 'none'
      }} />

        <div className="mt-6 pt-4 border-t space-y-4">
          <div className="flex items-center gap-4">
            <p className="text-xs text-muted-foreground">
              {content.length} characters
            </p>
            
            <div className="flex items-center gap-2">
              <input type="file" multiple accept="image/*" onChange={e => handlePhotoUpload(e.target.files)} className="hidden" id="photo-upload" disabled={uploadingPhoto} />
              <label htmlFor="photo-upload" className={cn("flex items-center gap-2 text-xs px-3 py-1 rounded-full border cursor-pointer transition-colors", uploadingPhoto ? 'opacity-50 cursor-not-allowed' : 'hover:bg-muted/50')}>
                <ImageIcon className="h-3 w-3" />
                {uploadingPhoto ? 'Uploading...' : 'Add Photos'}
              </label>
            </div>
          </div>

          {selectedPhotos.length > 0 && <div>
              <Label className="text-xs font-medium text-muted-foreground mb-2 block">
                Attached Photos ({selectedPhotos.length})
              </Label>
              <div className="flex flex-wrap gap-2">
                {selectedPhotos.map((photoUrl, index) => <div key={index} className="relative group">
                    <img src={photoUrl} alt={`Photo ${index + 1}`} className="w-16 h-16 object-cover rounded border" />
                    <button onClick={() => removePhoto(photoUrl)} className="absolute -top-1 -right-1 w-5 h-5 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <X className="h-3 w-3" />
                    </button>
                  </div>)}
              </div>
            </div>}
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setCurrentView('list')} disabled={isLoading}>
              Cancel
            </Button>
            <Button onClick={saveEntry} disabled={isLoading || !content.trim() && selectedPhotos.length === 0} className="flex items-center gap-2">
              <Save className="h-4 w-4" />
              {isLoading ? 'Saving...' : 'Save Entry'}
            </Button>
          </div>
        </div>
      </Card>
    </div>;
  const renderListView = () => <div className="space-y-6">
      <Card className="p-4">
        <div className="flex items-center justify-center">
          <div className="grid grid-cols-2 gap-2 bg-muted rounded-lg p-1.5 w-full">
            <Button 
              variant={journalMode === 'free-flow' ? 'default' : 'ghost'} 
              onClick={() => setJournalMode('free-flow')} 
              className="rounded-md h-auto py-3 px-4 flex flex-col items-center justify-center gap-1"
              style={{ color: 'hsl(181, 28%, 26%)' }}
            >
              <Sparkles className="h-5 w-5 shrink-0" style={{ color: 'hsl(181, 28%, 26%)' }} />
              <span className="text-sm font-medium whitespace-normal text-center leading-tight" style={{ color: 'hsl(181, 28%, 26%)' }}>Free Flow Writing</span>
            </Button>
            <Button 
              variant={journalMode === 'worry-diary' ? 'default' : 'ghost'} 
              onClick={() => setJournalMode('worry-diary')} 
              className="rounded-md h-auto py-3 px-4 flex flex-col items-center justify-center gap-1"
              style={{ color: 'hsl(181, 28%, 26%)' }}
            >
              <Moon className="h-5 w-5 shrink-0" style={{ color: 'hsl(181, 28%, 26%)' }} />
              <span className="text-sm font-medium whitespace-normal text-center leading-tight" style={{ color: 'hsl(181, 28%, 26%)' }}>Worry Diary</span>
            </Button>
          </div>
        </div>
        <p className="text-center text-xs text-muted-foreground mt-3">
          {journalMode === 'free-flow' 
            ? 'Express your thoughts freely to release mental clutter and gain clarity' 
            : 'A guided exercise to process worries with compassion and find peace'}
        </p>
      </Card>

      <div className="text-center">
        <div className="flex items-center justify-center gap-2 mb-3">
          <BookOpen className="h-6 w-6 text-primary" />
          <h3 className="text-2xl font-bold">Quantum Journal</h3>
          <SparkleGuide
            title="How to use your Journal"
            items={[
              { title: "Tag your mood", description: "Select how you're feeling when writing to track emotional patterns" },
              { title: "Search & filter", description: "Find entries by mood, date, or keywords" },
              { title: "Spot patterns", description: "Review entries to see recurring themes" },
              { title: "Two modes", description: "Free Flow for open expression, Worry Diary for guided processing" }
            ]}
          />
        </div>
        <p className="text-muted-foreground mb-6">
          {journalMode === 'free-flow' ? 'A free-flow space to write, release, and realign' : 'Your evening sanctuary to transform worry into wisdom'}
        </p>
        <Button onClick={startNewEntry} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          {journalMode === 'free-flow' ? 'New Entry' : 'Start Worry Diary'}
        </Button>
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        <div className="md:col-span-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search entries by title or content..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" />
          </div>
        </div>

        <div>
          <Select value={filterMood} onValueChange={setFilterMood}>
            <SelectTrigger className="bg-background border-2">
              <SelectValue placeholder="Filter by mood" />
            </SelectTrigger>
            <SelectContent className="z-50">
              <SelectItem value="all">All moods</SelectItem>
              {moods.map(mood => <SelectItem key={mood.value} value={mood.value}>
                  <div className="flex items-center gap-2">
                    <span className={mood.color}>●</span>
                    {mood.label}
                  </div>
                </SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="flex gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="flex-1 bg-background border-2">
                <CalendarIcon className="h-4 w-4 mr-2" />
                {filterDate ? format(filterDate, 'MMM d') : 'Filter date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 z-50" align="start">
              <Calendar mode="single" selected={filterDate} onSelect={setFilterDate} className={cn("p-3 pointer-events-auto")} />
              {filterDate && <div className="p-2 border-t">
                  <Button variant="secondary" size="sm" onClick={() => setFilterDate(undefined)} className="w-full">
                    Clear filter
                  </Button>
                </div>}
            </PopoverContent>
          </Popover>

          <Select value={sortBy} onValueChange={value => setSortBy(value as 'date' | 'title')}>
            <SelectTrigger className="w-auto">
              <SortDesc className="h-4 w-4" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="date">Sort by date</SelectItem>
              <SelectItem value="title">Sort by title</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-4">
        {isLoading ? <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="text-sm text-muted-foreground mt-2">Loading your entries...</p>
          </div> : filteredEntries.length === 0 ? <Card className="p-8 text-center">
            <BookOpen className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h4 className="font-semibold mb-2">
              {searchTerm || filterMood || filterDate ? 'No entries found' : 'Your journal awaits'}
            </h4>
            <p className="text-sm text-muted-foreground mb-4">
              {searchTerm || filterMood || filterDate ? 'Try adjusting your filters or search terms' : 'Start your first entry and let your thoughts flow freely'}
            </p>
            {!searchTerm && !filterMood && !filterDate && <Button onClick={startNewEntry} variant="outline">
                Write Your First Entry
              </Button>}
          </Card> : filteredEntries.map(entry => <Card key={entry.id} className="p-4 hover:shadow-gentle transition-all duration-200">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2 flex-1">
                  <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    {formatDate(entry.created_at)}
                  </span>
                  {entry.mood && <Badge variant="secondary" className="flex items-center gap-1">
                      {getMoodIcon(entry.mood)}
                      <span className="capitalize">{entry.mood}</span>
                    </Badge>}
                  {entry.photos && Array.isArray(entry.photos) && entry.photos.length > 0 && <Badge variant="outline" className="flex items-center gap-1">
                      <ImageIcon className="h-3 w-3" />
                      {entry.photos.length}
                    </Badge>}
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="secondary" size="sm" onClick={() => editEntry(entry)}>
                    Edit
                  </Button>
                  <Button variant="destructive" size="sm" onClick={() => deleteEntry(entry.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              {entry.title && <h4 className="font-semibold text-foreground mb-2 line-clamp-1">
                  {entry.title}
                </h4>}
              
              <div className="prose prose-sm max-w-none mb-3">
                <p className="text-foreground leading-relaxed line-clamp-3">
                  {entry.content}
                </p>
              </div>

              {entry.photos && Array.isArray(entry.photos) && entry.photos.length > 0 && <div className="flex flex-wrap gap-2 mb-3">
                  {entry.photos.slice(0, 3).map((photo, index) => <img key={index} src={photo} alt={`Entry photo ${index + 1}`} className="w-12 h-12 object-cover rounded border" />)}
                  {entry.photos.length > 3 && <div className="w-12 h-12 bg-muted rounded border flex items-center justify-center text-xs text-muted-foreground">
                      +{entry.photos.length - 3}
                    </div>}
                </div>}
              
              {(entry.content.length > 150 || entry.photos && Array.isArray(entry.photos) && entry.photos.length > 0) && <Button variant="secondary" size="sm" onClick={() => editEntry(entry)} className="mt-2 p-0 h-auto font-normal">
                  {entry.content.length > 150 ? 'Read more...' : 'View full entry...'}
                </Button>}
            </Card>)}
      </div>
    </div>;
  return <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-card rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-border shadow-xl">
        <div className="p-4 border-b flex justify-between items-center">
          <div className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-semibold">Quantum Journal</h2>
          </div>
          <Button variant="secondary" size="sm" onClick={onClose}>
            ✕
          </Button>
        </div>

        <div className="p-6">
          {currentView === 'list' && <ContextualNotificationPrompt context="journal" />}
          {currentView === 'list' ? renderListView() : currentView === 'worry-diary' ? renderWorryDiaryView() : renderWriteView()}
        </div>
      </div>
    </div>;
};
export default QuantumJournal;